/**
 * De confirmat — întrebările pe care aplicația nu le poate decide singură.
 *
 * De ce există (cerut de Dan, 27 aug 2026): din verificarea sesizărilor trimise
 * de mecanici au rămas zeci de lucruri care cer un om lângă utilaj — „e montat
 * OP000228?", „care din cele patru pagini e procedura?", „ce vaselină cumpărați
 * pentru «unsoare tip C»?". Stăteau în documente din `docs/`, pe care nu le
 * citește nimeni din fabrică.
 *
 * Pagina e făcută ca un test grilă, nu ca un formular: o întrebare, dovada de
 * ce e întrebată, două-șase variante, un clic. Se poate răspunde de pe telefon,
 * lângă utilaj — de aceea variantele sunt butoane mari, nu un `<select>`.
 *
 * Un răspuns NU schimbă nimic în scadențar. Rămâne o decizie înregistrată, cu
 * cine și când; aplicarea ei se face separat, cu backup și probă seacă.
 */

import { api, toast, escHtml } from './core';

interface Optiune { cod: string; eticheta: string; fel?: string }
interface Intrebare {
    id: number; cheie: string; categorie: string; categorie_eticheta: string;
    titlu: string; detaliu: string | null; dovada: string | null;
    machine_id: string | null; interval_id: number | null;
    document_id: number | null; page_number: number | null;
    optiuni: Optiune[]; raspuns: string | null; nota: string | null;
    raspuns_de: string | null; raspuns_la: string | null;
}
interface Raspuns {
    ok: boolean; intrebari: Intrebare[]; total: number; deschise: number; raspunse: number;
    contoare: Record<string, { total: number; deschise: number; eticheta: string }>;
}

let stareCurenta: 'deschise' | 'raspunse' | 'toate' = 'deschise';
let categorieCurenta = '';
let ultimul: Raspuns | null = null;

const CULORI: Record<string, string> = {
    bun: 'var(--ok,#4caf50)', rau: 'var(--danger,#e05252)', neutru: 'var(--text-muted)',
};

function cardIntrebare(q: Intrebare): string {
    const raspuns = q.raspuns;
    const aleasa = q.optiuni.find(o => o.cod === raspuns);
    const butoane = q.optiuni.map(o => {
        const activ = o.cod === raspuns;
        const culoare = CULORI[o.fel || 'neutru'] || CULORI.neutru;
        return `<button class="cf-optiune${activ ? ' aleasa' : ''}"
                    data-id="${q.id}" data-cod="${escHtml(o.cod)}"
                    style="--cf-culoare:${culoare}">
                    <span class="cf-bula"></span>${escHtml(o.eticheta)}</button>`;
    }).join('');

    const legaturi: string[] = [];
    if (q.machine_id) legaturi.push(`<span class="cf-eticheta">${escHtml(q.machine_id)}</span>`);
    if (q.interval_id) legaturi.push(`<span class="cf-eticheta">lucrarea #${q.interval_id}</span>`);
    if (q.document_id && q.page_number) {
        legaturi.push(`<a class="cf-eticheta cf-link" target="_blank"
            href="/api/documents/${q.document_id}/view?page=${q.page_number}">📖 manual p.${q.page_number}</a>`);
    }

    return `
    <article class="cf-card${raspuns ? ' raspunsa' : ''}" data-id="${q.id}">
        <header class="cf-cap">
            <h3 class="cf-titlu">${escHtml(q.titlu)}</h3>
            <div class="cf-etichete">${legaturi.join('')}</div>
        </header>
        ${q.detaliu ? `<p class="cf-detaliu">${escHtml(q.detaliu)}</p>` : ''}
        ${q.dovada ? `<p class="cf-dovada">${escHtml(q.dovada)}</p>` : ''}
        <div class="cf-optiuni">${butoane}</div>
        <div class="cf-jos">
            <input type="text" class="cf-nota" data-id="${q.id}" maxlength="1000"
                   placeholder="Observație (nu e obligatorie)" value="${escHtml(q.nota || '')}">
            ${raspuns ? `<span class="cf-semnat">✓ ${escHtml(aleasa ? aleasa.eticheta : raspuns)}
                 — ${escHtml(q.raspuns_de || '')} ${escHtml((q.raspuns_la || '').slice(0, 16))}
                 <button class="cf-sterge" data-id="${q.id}">schimbă</button></span>` : ''}
        </div>
    </article>`;
}

function randeaza(d: Raspuns): void {
    const sec = document.getElementById('sec-confirmari');
    if (!sec) return;
    const procent = d.total ? Math.round((d.raspunse / d.total) * 100) : 0;

    const filtreCategorii = Object.entries(d.contoare)
        .sort((a, b) => b[1].deschise - a[1].deschise)
        .map(([cod, c]) => `<button class="cf-filtru${categorieCurenta === cod ? ' activ' : ''}"
             data-categorie="${escHtml(cod)}">${escHtml(c.eticheta)}
             <b>${c.deschise}</b></button>`).join('');

    const grupuri: Record<string, Intrebare[]> = {};
    for (const q of d.intrebari) {
        const cheie = q.categorie_eticheta || q.categorie;
        (grupuri[cheie] = grupuri[cheie] || []).push(q);
    }
    const corp = Object.keys(grupuri).length
        ? Object.entries(grupuri).map(([eticheta, lista]) => `
            <section class="cf-grup">
                <h2 class="cf-grup-titlu">${escHtml(eticheta)}
                    <span class="cf-grup-numar">${lista.length}</span></h2>
                ${lista.map(cardIntrebare).join('')}
            </section>`).join('')
        : `<p class="cf-gol">Nimic aici. ${stareCurenta === 'deschise'
              ? 'Toate întrebările au primit răspuns.' : ''}</p>`;

    sec.innerHTML = `
    <div class="cf-antet">
        <div>
            <h1 class="cf-h1">De confirmat</h1>
            <p class="cf-subtitlu">Lucruri pe care aplicația nu le poate hotărî din manual.
               Bifează când treci pe lângă utilaj — nimic nu se schimbă automat.</p>
        </div>
        <div class="cf-progres" title="${d.raspunse} din ${d.total}">
            <div class="cf-progres-bara"><i style="width:${procent}%"></i></div>
            <span class="cf-progres-text">${d.raspunse} din ${d.total} (${procent}%)</span>
        </div>
    </div>
    <div class="cf-bara">
        <div class="cf-stari">
            <button class="cf-stare${stareCurenta === 'deschise' ? ' activ' : ''}" data-stare="deschise">De răspuns <b>${d.deschise}</b></button>
            <button class="cf-stare${stareCurenta === 'raspunse' ? ' activ' : ''}" data-stare="raspunse">Răspunse <b>${d.raspunse}</b></button>
            <button class="cf-stare${stareCurenta === 'toate' ? ' activ' : ''}" data-stare="toate">Toate</button>
        </div>
        <div class="cf-filtre">
            <button class="cf-filtru${categorieCurenta === '' ? ' activ' : ''}" data-categorie="">Toate felurile</button>
            ${filtreCategorii}
        </div>
    </div>
    ${corp}`;
}

async function incarca(): Promise<void> {
    const sec = document.getElementById('sec-confirmari');
    if (sec && !ultimul) sec.innerHTML = '<p class="cf-gol">Se încarcă…</p>';
    try {
        const p = new URLSearchParams({ stare: stareCurenta });
        if (categorieCurenta) p.set('categorie', categorieCurenta);
        const d: Raspuns = await api('/api/confirmari?' + p.toString());
        ultimul = d;
        randeaza(d);
    } catch (e: any) {
        if (sec) sec.innerHTML = `<p class="cf-gol">Nu s-au putut încărca întrebările: ${escHtml(e?.message || String(e))}</p>`;
    }
}

async function raspunde(id: number, cod: string | null): Promise<void> {
    const nota = (document.querySelector(`.cf-nota[data-id="${id}"]`) as HTMLInputElement | null)?.value || '';
    try {
        const r = await api(`/api/confirmari/${id}/raspuns`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ optiune: cod || '', nota }),
        });
        if (!r || r.ok === false) throw new Error(r?.error || 'Răspunsul nu s-a salvat');
        toast(cod ? 'Răspuns salvat' : 'Răspuns șters', 'success');
        await incarca();
    } catch (e: any) {
        toast(e?.message || 'Eroare la salvare', 'error');
    }
}

export function initConfirmari(): void {
    const sec = document.getElementById('sec-confirmari');
    if (!sec) return;
    if (!(sec as any)._cfLegat) {
        (sec as any)._cfLegat = true;
        sec.addEventListener('click', (ev: Event) => {
            const t = ev.target as HTMLElement;
            const opt = t.closest('.cf-optiune') as HTMLElement | null;
            if (opt) {
                void raspunde(Number(opt.dataset.id), opt.dataset.cod || '');
                return;
            }
            const sterge = t.closest('.cf-sterge') as HTMLElement | null;
            if (sterge) {
                void raspunde(Number(sterge.dataset.id), null);
                return;
            }
            const stare = t.closest('.cf-stare') as HTMLElement | null;
            if (stare) {
                stareCurenta = (stare.dataset.stare as any) || 'deschise';
                void incarca();
                return;
            }
            const filtru = t.closest('.cf-filtru') as HTMLElement | null;
            if (filtru) {
                categorieCurenta = filtru.dataset.categorie || '';
                void incarca();
            }
        });
        /* Nota se salvează SINGURĂ la părăsirea câmpului (4 sep 2026). Până acum
         * se citea doar când se apăsa o variantă, deci ce scria inginerul DUPĂ
         * ce alegea se pierdea: 32 din 33 de răspunsuri au ajuns cu „—", iar
         * trei „îl scriu mai jos" au rămas fără produs. Nu reîncărcăm lista —
         * omul poate fi în mijlocul altui card. */
        sec.addEventListener('change', (ev: Event) => {
            const inp = ev.target as HTMLInputElement;
            if (!inp.classList.contains('cf-nota')) return;
            const id = Number(inp.dataset.id);
            void api(`/api/confirmari/${id}/raspuns`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ doar_nota: true, nota: inp.value }),
            }).then(() => toast('Observația a fost salvată', 'success'))
              .catch((e: any) => toast(e?.message || 'Observația nu s-a salvat', 'error'));
        });
    }
    void incarca();
}
